/**
 * Derive an offer's `visibility` from its parent location's `visibility`, mirroring the pipeline
 * writer's own `deriveOfferVisibilityFromLocation` (`scripts/camp-data/_reconcile/offerWriteCore.cjs`).
 * Only a `v_published` location earns a published offer; everything else (draft, unpublished,
 * archived, kbOnly, missing) is conservative by design (KB-1359 Decision 2) — a draft/unpublished
 * location must never silently emit a published offer. Search is the console-installed
 * firestore-algolia-search extension mirroring `offers` wholesale, so a published offer is publicly
 * searchable within seconds regardless of which writer produced it (KB-2383).
 *
 * Shared via the `@/functions-shared` alias (tsconfig + vite) so BOTH the server
 * (`offerDataPreparationServer.ts`) and client (`offerDataPreparation.ts`) offer-prep seams derive
 * the identical value from a single implementation — no lockstep-drift risk between them.
 */
export function deriveOfferVisibility(
  locationVisibility: unknown,
  opts?: { rollbackUnpublished?: boolean },
): "published" | "draft" {
  // KB-3641 — the rollback-unpublish hold. An offer an operator un-created stays `draft` whatever
  // its parent says, so a republish cannot silently undo the un-create. The marker is
  // `offers.rollbackUnpublishedAt`, written only by the rollback path
  // (`scripts/camp-data/_operator/rollbackAiWritesCore.cjs`).
  //
  // ⚠️ The pipeline deriver `deriveOfferVisibilityFromLocation` does NOT take this hold — it is
  // called before the stored offer is in scope, so it cannot. Do not read this as mirroring it.
  // Invariant 10a ("Unpublished by rollback") is in the KNOWLEDGE repo
  // (`kidsbooked-knowledge/standards/data-models/firestore-data-model.md`), not in this one.
  //
  // It can only ever make an offer LESS visible, so invariant 10's rule is untouched. The parameter
  // is OPTIONAL and omitting it is byte-identical to the pre-KB-3641 behaviour.
  //
  // ⚠️ BOTH mirrors pass it, and the client one MUST. `updateOffer` (`src/services/offer/offerCrud.ts`)
  // reads the stored doc and spreads it as `{ ...currentOffer, ...offerData }` before calling
  // `prepareOfferData`, so the client path DOES hold the stored offer — an earlier version of this
  // comment claimed it did not, and that false premise is how an unrelated admin edit came to
  // republish a rolled-back offer. `offerDataPreparation.ts` and `offerDataPreparationServer.ts`
  // both pass the hold now; `offerBatchUpdateService` shares the client seam.
  if (opts?.rollbackUnpublished) return "draft";
  return String(locationVisibility || "").trim() === "v_published" ? "published" : "draft";
}
